import React from 'react';
import { createRoot } from 'react-dom/client';
import { useWidgetProps, useOpenAIGlobal, useOpenAIActions } from 'react-openai-apps-sdk';
import { SafeArea } from '@/components/SafeArea';
import { BrandProfileHeader } from './components/BrandProfileHeader';
import { ColorPalette } from './components/ColorPalette';
import { BrandVoice } from './components/BrandVoice';
import { ContentStrategy } from './components/ContentStrategy';
import { PlatformConnections } from './components/PlatformConnections';
import { ExportBrandProfileDialog } from './components/ExportBrandProfileDialog';
import { useBrandProfileExport } from './hooks/useBrandProfileExport';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Brand Profile Widget Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <SafeArea fallbackHeight={400}>
          <div className="flex items-center justify-center min-h-[400px] p-4">
            <div className="text-center max-w-md">
              <div className="text-destructive text-lg font-semibold mb-2">
                Something went wrong
              </div>
              <div className="text-sm text-muted-foreground mb-4">
                {this.state.error?.message || 'An error occurred while loading the brand profile'}
              </div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Reload Widget
              </button>
            </div>
          </div>
        </SafeArea>
      );
    }

    return this.props.children;
  }
}

// Mock test data for development
function generateTestData() {
  return {
    id: 'brand-test-123',
    name: 'Acme Corporation',
    slug: 'acme-corporation',
    websiteUrl: 'https://acme.com',
    logoUrl: 'https://logo.clearbit.com/acme.com',
    industry: 'E-commerce',
    description: 'Leading e-commerce platform for modern businesses. We help companies scale their online presence with innovative tools and data-driven insights.',

    // Visual Identity
    primaryColor: '#FF6B6B',
    secondaryColor: '#4ECDC4',
    accentColor: '#FFD93D',
    fontFamily: 'Inter, sans-serif',

    // Brand Voice
    brandVoice: {
      tone: ['professional', 'innovative', 'friendly', 'data-driven'],
      personality: 'Tech-forward B2B leader that values transparency and innovation',
      dosList: [
        'Use data-driven insights',
        'Focus on ROI and business value',
        'Be specific and concrete',
        'Highlight customer success stories',
      ],
      dontsList: [
        'Avoid buzzwords without substance',
        'No emoji overload',
        'Don\'t make unsubstantiated claims',
        'Avoid overly casual language',
      ],
    },

    // Content Strategy
    topics: ['SaaS', 'E-commerce', 'Digital Transformation', 'B2B Marketing', 'Analytics'],
    targetAudience: 'B2B marketing teams at mid-sized to enterprise companies',
    contentPillars: [
      'Product Education',
      'Thought Leadership',
      'Customer Success Stories',
      'Industry Insights',
    ],

    // Platforms
    platforms: [
      {
        platform: 'linkedin',
        username: '@acmecorp',
        pageName: 'Acme Corporation',
      },
      {
        platform: 'twitter',
        username: '@acme',
        pageName: 'Acme',
      },
      {
        platform: 'facebook',
        username: '@acmecorp',
        pageName: 'Acme Corporation',
      },
      {
        platform: 'instagram',
        username: '@acmecorp',
        pageName: 'Acme Corporation',
      },
    ],

    // Metadata
    createdAt: new Date('2025-01-10'),
    updatedAt: new Date('2025-01-15'),
  };
}

interface BrandProfile {
  id: string;
  name: string;
  slug: string;
  websiteUrl?: string;
  logoUrl?: string;
  industry?: string;
  description?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  brandVoice?: {
    tone: string[];
    personality: string;
    dosList: string[];
    dontsList: string[];
  };
  topics?: string[];
  targetAudience?: string;
  contentPillars?: string[];
  platforms?: Array<{
    platform: string;
    username?: string;
    pageName?: string;
    pageId?: string;
  }>;
  createdAt: Date | string;
  updatedAt: Date | string;
}

function BrandProfileWidget() {
  // In dev mode, use mock data. In production, use tool output
  const brandProfile = useWidgetProps<BrandProfile>(() =>
    import.meta.env.DEV ? generateTestData() : null
  );

  const displayMode = useOpenAIGlobal('displayMode') as 'inline' | 'fullscreen' | undefined;
  const isExpanded = displayMode === 'fullscreen';
  const { requestDisplayMode, sendFollowUpMessage } = useOpenAIActions();

  // Export hook - handles PDF/PNG generation and upload
  const {
    isExporting,
    exportDialogOpen,
    setExportDialogOpen,
    exportDialogType,
    exportDialogData,
    currentUploadedUrl,
    brandProfileRef,
    handleExportImage,
    handleExportPDF,
  } = useBrandProfileExport({ brandProfile: brandProfile! });

  if (!brandProfile) {
    return (
      <SafeArea fallbackHeight={400}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-muted-foreground mb-2">No brand profile data</div>
            <div className="text-sm text-muted-foreground">
              Brand profile will appear here when loaded
            </div>
          </div>
        </div>
      </SafeArea>
    );
  }

  const handleFullscreenToggle = async () => {
    try {
      const newMode = isExpanded ? 'inline' : 'fullscreen';
      await requestDisplayMode({ mode: newMode });
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
    }
  };

  const handleCopyLink = () => {
    try {
      const link = `brand://profile/${brandProfile.slug}`;
      navigator.clipboard.writeText(link);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleEdit = async () => {
    try {
      await sendFollowUpMessage({
        prompt: `I want to edit the brand profile for "${brandProfile.name}". Show me the current details and help me update them.`,
      });
    } catch (error) {
      console.error('Failed to send edit message:', error);
    }
  };

  const handleSendViaEmail = async () => {
    try {
      if (!currentUploadedUrl) return;
      const fileType = exportDialogType === 'pdf' ? 'brand guidelines PDF' : 'brand profile image';
      await sendFollowUpMessage({
        prompt: `Send this ${fileType} for "${brandProfile.name}" via email: ${currentUploadedUrl}`,
      });
    } catch (error) {
      console.error('Failed to send email message:', error);
    }
  };

  return (
    <>
      <SafeArea
        className="w-full bg-background"
        fallbackHeight={isExpanded ? 800 : 600}
      >
        <div className="flex flex-col h-full">
          {/* Header with actions */}
          <BrandProfileHeader
            brandName={brandProfile.name}
            slug={brandProfile.slug}
            isExpanded={isExpanded}
            isExporting={isExporting}
            onFullscreenClick={handleFullscreenToggle}
            onExportPDF={handleExportPDF}
            onExportImage={handleExportImage}
            onCopyLink={handleCopyLink}
            onEdit={handleEdit}
          />

          {/* Scrollable content - ref attached for export */}
          <div ref={brandProfileRef} className={`flex-1 overflow-y-auto ${isExpanded ? 'p-6' : 'p-4'}`}>
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Brand info section */}
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div className="flex-shrink-0">
                  {brandProfile.logoUrl ? (
                    <img
                      src={brandProfile.logoUrl}
                      alt={`${brandProfile.name} logo`}
                      className={`rounded-lg object-cover bg-muted ${isExpanded ? 'w-20 h-20' : 'w-16 h-16'}`}
                    />
                  ) : (
                    <div className={`rounded-lg bg-primary/10 flex items-center justify-center ${isExpanded ? 'w-20 h-20' : 'w-16 h-16'}`}>
                      <span className={`font-bold text-primary ${isExpanded ? 'text-3xl' : 'text-2xl'}`}>
                        {brandProfile.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Brand details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    {brandProfile.industry && (
                      <>
                        <span className="font-medium">{brandProfile.industry}</span>
                        <span>•</span>
                      </>
                    )}
                    {brandProfile.websiteUrl && (
                      <a
                        href={brandProfile.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {brandProfile.websiteUrl.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>

                  {brandProfile.description && (
                    <p className="text-sm text-foreground leading-relaxed">
                      {brandProfile.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Color Palette */}
              <ColorPalette
                primaryColor={brandProfile.primaryColor}
                secondaryColor={brandProfile.secondaryColor}
                accentColor={brandProfile.accentColor}
                fontFamily={brandProfile.fontFamily}
              />

              {/* Brand Voice */}
              <BrandVoice
                tone={brandProfile.brandVoice?.tone}
                personality={brandProfile.brandVoice?.personality}
                dosList={brandProfile.brandVoice?.dosList}
                dontsList={brandProfile.brandVoice?.dontsList}
              />

              {/* Content Strategy */}
              <ContentStrategy
                topics={brandProfile.topics}
                targetAudience={brandProfile.targetAudience}
                contentPillars={brandProfile.contentPillars}
              />

              {/* Platform Connections */}
              <PlatformConnections platforms={brandProfile.platforms} />

              {/* Metadata footer */}
              {isExpanded && (
                <div className="pt-4 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
                  <div>
                    Created: {new Date(brandProfile.createdAt).toLocaleDateString()}
                  </div>
                  <div>
                    Updated: {new Date(brandProfile.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </SafeArea>

      {/* Export dialog - shown when export data is available */}
      <ExportBrandProfileDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        uploadedUrl={currentUploadedUrl}
        filename={exportDialogData?.filename || null}
        exportType={exportDialogType || 'image'}
        onSendViaEmail={handleSendViaEmail}
      />
    </>
  );
}

// Mount widget with error boundary
const root = document.getElementById('brand-profile-root');
if (root) {
  createRoot(root).render(
    <ErrorBoundary>
      <BrandProfileWidget />
    </ErrorBoundary>
  );
}
